/**
 * vip.sina.com — TEK sayfa (2026-09-16 operatör isteği): stok, kimlik doğrulama,
 * site başına satışlar ve mailbox başına "hangi siteler için satıldı" detayı,
 * import ve yenile — hepsi burada. (Alias-model şablonu bilinçli olarak
 * kullanılmaz: sinavip'da alias yok, satış hakkı (mailbox, site) bazındadır.)
 */
import { useCallback, useEffect, useState } from 'react';

import { useCan } from '../../session/can.js';
import { BulkImportModal } from '../mailcom/BulkImportModal.js';
import { MailboxUnitDashboard } from '../mailbox-unit/MailboxUnitDashboard.js';
import { MailboxUnitManager } from '../mailbox-unit/MailboxUnitManager.js';
import {
  availableShare,
  fetchSinavipSiteUsage,
  type SinavipSiteUsage,
} from './siteUsage.js';

import '../../styles/mailboxunit.mobile.css';

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ok'; readonly data: SinavipSiteUsage }
  | { readonly kind: 'error'; readonly message: string };



export function SinavipPage(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const canManage = useCan('provider:manage_inventory');
  const [showImport, setShowImport] = useState(false);

  // `background`: a refresh after a manager mutation keeps the page (and so
  // the manager with its page / filters / feedback) mounted instead of
  // flipping to the loading state, which unmounted it and reset it to page 1.
  const load = useCallback(async (signal?: AbortSignal, opts?: { readonly background?: boolean }) => {
    if (opts?.background === true) setState((prev) => (prev.kind === 'ok' ? prev : { kind: 'loading' }));
    else setState({ kind: 'loading' });
    const res = await fetchSinavipSiteUsage(signal);
    if (res.kind === 'ok') {
      setState({ kind: 'ok', data: res.data });
      return;
    }
    if (res.kind === 'auth_required') {
      setState({ kind: 'error', message: 'Oturum gerekli — lütfen admin girişi yapın.' });
      return;
    }
    if (res.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Yetki yok: provider:inspect izni gerekiyor.' });
      return;
    }
    if (res.kind === 'network_error') {
      setState({ kind: 'error', message: 'Bağlantı hatası: ' + res.message });
      return;
    }
    setState({ kind: 'error', message: 'İstek başarısız (' + res.status + ': ' + res.reason + ').' });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);


  return (
    <section className="mbu-dashboard" data-testid="sinavip-dashboard">
      <header className="sinavip-head mbu-head">
        <div>
          <h1>vip.sina.com — stok, kimlik ve site kullanımı</h1>
          <p>
            Her satır bir mailbox'tır (alias üretilmez). Satış hakkı{' '}
            <strong>(mailbox, site)</strong> bazındadır: posta gelince o çift
            herkese kalıcı kapanır; posta gelmezse mailbox hemen başka müşterilere
            satılabilir, aynı müşteriye aynı site için bir daha satılmaz (D-247).
            Mailbox başka siteler için satılmaya devam eder. Bir mailbox,
            kimliği doğrulanmadan satılmaz.
          </p>
        </div>
        <div className="sinavip-head-actions mbu-head-actions">
          {state.kind === 'ok' && (
            <span className="badge badge-neutral">
              {state.data.accounts} hesap · {state.data.units.total} unit
            </span>
          )}
          {canManage ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowImport(true)}
              data-testid="sinavip-import"
              title="email:şifre satırları — mailbox'ın kendisi unit olarak eklenir; şifre ':' içerebilir"
            >
              Import / TXT
            </button>
          ) : null}
          <button type="button" className="btn btn-secondary" onClick={() => void load()} data-testid="sinavip-refresh">
            Yenile
          </button>
        </div>
      </header>
      {canManage ? (
        <BulkImportModal
          providerId="sinavip"
          providerLabel="vip.sina.com"
          open={showImport}
          onClose={() => setShowImport(false)}
          onCompleted={() => void load()}
        />
      ) : null}

      {state.kind === 'loading' && (
        <div className="card">
          <div className="card-body" data-testid="sinavip-loading">Yükleniyor…</div>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="card">
          <div className="card-body" role="alert" data-testid="sinavip-error">
            <span className="badge badge-danger" style={{ marginRight: 8 }}>Hata</span>
            {state.message}
          </div>
        </div>
      )}

      {state.kind === 'ok' && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
              <div className="stat" data-testid="kpi-leased">
                <div className="stat-label">Kiralamada</div>
                <div className="stat-value">{state.data.units.leased}</div>
                <div className="stat-meta">şu an müşteride</div>
              </div>
              <div className="stat" data-testid="kpi-verified">
                <div className="stat-label">Kimlik doğrulandı</div>
                <div className="stat-value">{state.data.probe?.verified ?? '—'}</div>
                <div className="stat-meta">
                  {state.data.probe?.pending ?? 0} bekliyor · {state.data.probe?.quarantined ?? 0} karantina
                </div>
              </div>
              <div className="stat" data-testid="kpi-accounts">
                <div className="stat-label">Hesap</div>
                <div className="stat-value">{state.data.accounts}</div>
                <div className="stat-meta">import edilmiş sinavip hesabı</div>
              </div>
          </div>

          <MailboxUnitDashboard providerId="sinavip" providerLabel="vip.sina.com" />

          <MailboxUnitManager
            providerId="sinavip"
            providerLabel="vip.sina.com"
            onChanged={() => void load(undefined, { background: true })}
          />

          <div className="card">
            <div className="card-body">
              <div className="card-title" style={{ marginBottom: 6 }}>Kapasite</div>
              <p className="sinavip-footnote" data-testid="sinavip-capacity-note">
                {state.data.units.total} mailbox × sınırsız site hakkı. Şu an{' '}
                <strong>{state.data.units.available}</strong> mailbox satılabilir (%{availableShare(state.data.units)});
                yanmış çiftler yalnızca kendi sitesi için kapalı, mailbox'ın kendisi kapanmaz.
                Import edilen bir mailbox, kimlik doğrulaması (dakikada 10 hesap) geçince satışa açılır.
              </p>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
